/**
 * Imperative subtitle painter — ports extension
 * `adapters/dom/overlayRenderer.ts` `render` / `renderText` / `syncTextDragHandle`
 * / learning tokens / hit-testing (image cues intentionally omitted — YouTube N/A).
 */
import type { Cue } from '@core/model/cue.ts';
import {
  clampTopToFloor,
  clampUnifiedTextSlotTopsToFloor,
  computeUnifiedTextSlotTops,
  subtitleContentFloor,
  type BaselineLayout,
} from '@core/layout.ts';
import type { Settings } from '@core/settings/schema.ts';
import { paintTrackStyle, syncGoogleFont } from '@core/styles/applyTrackStyle.ts';
import { trackStyleFromSettings } from '@core/styles/stylePresets.ts';
import { paintDubbingOverlay } from '../../../../extension/src/adapters/dom/dubbingOverlay.ts';
import type { OverlayRenderModel } from '../../../../extension/src/adapters/dom/overlayRenderer.ts';
import { toTrustedHtml } from '../../../../extension/src/adapters/dom/trustedTypes.ts';
import {
  createSubtitleDragHandle,
  SUBTITLE_DRAG_HANDLE_CLASS,
} from '../../../../extension/src/adapters/dom/subtitlePositionDrag.ts';
import { attachNativeContextMenuGuard } from '../../../../extension/src/adapters/dom/nativeContextMenu.ts';
import {
  getRenderText,
} from '../../core/renderText.ts';
import { isPracticeLearningSlot } from '../../features/learning/learningHoverPause.ts';
import { segmentSubtitleHtml } from '../../features/learning/segmentSubtitleText.ts';
import type { SubtitleSlot } from '../../state/subtitleStore.ts';

export type OverlayPaintModel = {
  slot: SubtitleSlot;
  cues: Cue[];
  hasOtherSlot: boolean;
  hidden: boolean;
  dualMode: boolean;
  baseline: BaselineLayout;
  settings: Settings;
  loadingText?: string;
  /** BCP-47-ish locale for learning-mode Intl.Segmenter. */
  lang?: string;
  /** Required when practiceMode is shadowing — drives horizontal ticker position. */
  currentTimeMs?: number;
  /**
   * Client rect of the playback box. Shadowing maps this into the overlay host
   * so left/right edges follow the picture.
   */
  playbackClientRect?: { left: number; top: number; width: number; height: number };
};

/** Sum `.textBasedSub` stack height including inter-cue margins (excludes drag handle). */
export function measureTextBasedHeight(container: HTMLElement | null): number {
  if (!container) return 0;
  const nodes = container.querySelectorAll('.textBasedSub');
  if (nodes.length === 0) return 0;
  const first = nodes[0]?.getBoundingClientRect();
  const last = nodes[nodes.length - 1]?.getBoundingClientRect();
  if (!first || !last) return 0;
  return Math.max(0, last.bottom - first.top);
}

function slotNodeId(slot: SubtitleSlot): 'mainSub' | 'secondSub' {
  return slot === 'main' ? 'mainSub' : 'secondSub';
}

/** Tokenize only the practice learning track (dictationSubtitle), not both overlays. */
export function learningTokensForSlot(settings: Settings, slot: SubtitleSlot): boolean {
  return Boolean(
    settings.learningMode
    && !settings.dictationMode
    && isPracticeLearningSlot(settings, slot),
  );
}

/**
 * Cue layer must receive hits for selection, learning tokens, or dictation.
 * Otherwise pass clicks through to player controls (drag handle stays interactive via CSS).
 */
export function needsSubtitleHitTesting(
  settings: Settings,
  learningTokens: boolean,
): boolean {
  if (settings.dictationMode) return true;
  if (learningTokens) return true;
  if (settings.selectable && !learningTokens) return true;
  return false;
}

export class OverlayPainter {
  private root: HTMLElement;

  constructor(root: HTMLElement, private loadingText = 'Loading…') {
    this.root = root;
  }

  setRoot(root: HTMLElement): void {
    this.root = root;
  }

  clear(slot?: SubtitleSlot): void {
    if (slot) {
      this.root.ownerDocument.getElementById(slotNodeId(slot))?.remove();
      return;
    }
    this.root.ownerDocument.getElementById('mainSub')?.remove();
    this.root.ownerDocument.getElementById('secondSub')?.remove();
  }

  render(model: OverlayPaintModel): void {
    const container = this.ensureContainer(model);
    const isShadowing = model.settings.practiceMode === 'shadowing';

    if (!isShadowing) {
      container.classList.remove('unisubs-dubbing');
      container.removeAttribute('data-unisubs-dubbing-sig');
      container.style.overflow = '';
      const handle = container.querySelector(`.${SUBTITLE_DRAG_HANDLE_CLASS}`) as HTMLElement | null;
      if (handle) handle.style.left = '';
      container.replaceChildren();
    }

    if (model.hidden) {
      if (isShadowing) container.replaceChildren();
      return;
    }

    if (isShadowing) {
      paintDubbingOverlay(
        container,
        model as OverlayRenderModel,
        model.currentTimeMs ?? 0,
        {
          loadingText: model.loadingText ?? this.loadingText,
          getRenderText,
          syncTextDragHandle: (el, m) => this.syncTextDragHandle(el, m as OverlayPaintModel),
        },
      );
      return;
    }

    container.replaceChildren();
    this.renderText(container, model);
  }

  private ensureContainer(model: OverlayPaintModel): HTMLElement {
    const id = slotNodeId(model.slot);
    const doc = this.root.ownerDocument;
    const learningTokens = learningTokensForSlot(model.settings, model.slot);
    const nativeSelectable = model.settings.selectable && !learningTokens;
    let container = doc.getElementById(id);

    if (container && container.parentElement !== this.root) {
      container.remove();
      container = null;
    }
    if (!container) {
      container = doc.createElement('div');
      container.id = id;
      container.setAttribute('data-testid', id);
      this.root.appendChild(container);
    }

    const showSelectable = nativeSelectable || model.settings.dictationMode;
    const passThrough = !needsSubtitleHitTesting(model.settings, learningTokens);
    container.classList.toggle('selectable', showSelectable);
    container.classList.toggle('unisubs-learning', learningTokens);
    container.classList.toggle('unisubs-sub-layer', true);
    container.classList.toggle('unisubs-sub-pass-through', passThrough);
    if (showSelectable) {
      attachNativeContextMenuGuard(container);
    }
    container.style.position = 'absolute';
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.pointerEvents = 'none';
    // Stay inside .subtitle-overlay stacking context — do not match stage-rails
    // z-index (2147483647) or cue layers steal edge/center taps after paint.
    container.style.zIndex = '';
    container.style.cursor = '';
    return container;
  }

  private appendLearningTokens(
    parent: HTMLElement,
    html: string,
    locale: string | undefined,
    segmentText?: string,
  ): void {
    const segments = segmentSubtitleHtml(html, locale, { segmentText });
    parent.style.whiteSpace = 'pre-wrap';
    segments.forEach((seg, i) => {
      const el = this.root.ownerDocument.createElement('span');
      const displayHtml = seg.html ?? seg.text;
      if (displayHtml.includes('<')) {
        el.innerHTML = toTrustedHtml(displayHtml);
      } else {
        el.textContent = seg.text;
      }
      el.dataset.tokenIndex = String(i);
      el.dataset.plain = seg.plain ?? seg.text;
      el.className = seg.isWordLike ? 'unisubs-token' : 'unisubs-token-gap';
      parent.appendChild(el);
    });
  }

  private renderText(container: HTMLElement, model: OverlayPaintModel): void {
    const settings = model.settings;
    const baseline = model.baseline;
    const trackStyle = trackStyleFromSettings(settings, model.slot);
    const spacing = baseline.viewHeight * settings.spacing;
    let totalWidth = 0;
    const learningTokens = learningTokensForSlot(settings, model.slot);

    container.style.inset = '';
    container.style.right = '0px';
    container.style.flexFlow = 'column';
    container.style.width = 'fit-content';

    syncGoogleFont(
      model.slot,
      trackStyle.fontGoogleFamilies,
      trackStyle.fontGoogleCssProperties,
      this.root.ownerDocument,
    );

    const dualMode = model.dualMode;
    const loadingText = model.loadingText ?? this.loadingText;

    model.cues.forEach((cue, index) => {
      const div = this.root.ownerDocument.createElement('div');
      div.classList.add('textBasedSub');
      paintTrackStyle(div, trackStyle, {
        viewHeight: baseline.viewHeight,
        maxWidthPx: baseline.viewWidth * settings.maxWidth,
        googleSlot: model.slot,
      });
      if (index > 0) div.style.marginTop = '5px';
      div.setAttribute('data-testid', `cue-${model.slot}-${index}`);

      const span = this.root.ownerDocument.createElement('span');
      span.style.display = 'inline-block';
      span.dir = 'auto';
      const html = getRenderText(cue, dualMode, {
        loadingText,
        speakingMode: settings.speakingMode,
      });

      if (learningTokens) {
        const segmentText =
          dualMode && cue.translation
            ? `${cue.text}\n${cue.translation}`
            : cue.text;
        this.appendLearningTokens(span, html, model.lang, segmentText);
      } else {
        // Extension always uses trusted innerHTML (never page defaultPolicy).
        span.innerHTML = toTrustedHtml(html);
      }
      div.appendChild(span);
      container.appendChild(div);

      totalWidth = Math.max(totalWidth, div.getBoundingClientRect().width);
    });

    const totalHeight = measureTextBasedHeight(container);
    this.syncTextDragHandle(container, model);

    container.style.left = `${
      (baseline.spaceWidth * 2 + baseline.viewWidth) / 2 - totalWidth / 2
    }px`;

    const floorY = subtitleContentFloor(baseline.viewBottom);
    const doc = this.root.ownerDocument;

    if (settings.subtitleSeparation) {
      let top: number;
      if (model.slot === 'main') {
        top = model.hasOtherSlot
          ? baseline.top - totalHeight - spacing
          : baseline.top - totalHeight / 2;
      } else {
        top = model.hasOtherSlot
          ? baseline.top + spacing
          : baseline.top - totalHeight / 2;
      }
      container.style.top = `${clampTopToFloor(top, totalHeight, floorY)}px`;
      return;
    }

    const mainEl = doc.getElementById('mainSub');
    const secondEl = doc.getElementById('secondSub');
    const mainHeight =
      model.slot === 'main'
        ? totalHeight
        : model.hasOtherSlot
          ? measureTextBasedHeight(mainEl)
          : null;
    const secondHeight =
      model.slot === 'second'
        ? totalHeight
        : model.hasOtherSlot
          ? measureTextBasedHeight(secondEl)
          : null;

    const tops = clampUnifiedTextSlotTopsToFloor(
      computeUnifiedTextSlotTops({
        baselineTop: baseline.top,
        spacing,
        mainHeight,
        secondHeight,
      }),
      { mainHeight, secondHeight },
      floorY,
    );

    if (tops.mainTop !== undefined && mainEl) {
      mainEl.style.top = `${tops.mainTop}px`;
    }
    if (tops.secondTop !== undefined && secondEl) {
      secondEl.style.top = `${tops.secondTop}px`;
    }
  }

  /**
   * Separated: grip on this slot when it has cues.
   * Shared subY: exactly one grip for the stack — prefer main when it has
   * visible cues, otherwise second.
   * Idempotent: do not remove/recreate an already-correct handle (shadowing
   * paints every frame; recreating kills :hover and causes flicker).
   */
  private syncTextDragHandle(container: HTMLElement, model: OverlayPaintModel): void {
    const settings = model.settings;
    const doc = this.root.ownerDocument;
    const handleIn = (el: HTMLElement | null): HTMLElement | null => (
      el?.querySelector(`.${SUBTITLE_DRAG_HANDLE_CLASS}`) as HTMLElement | null
    );
    const removeHandle = (el: HTMLElement | null): void => {
      handleIn(el)?.remove();
    };
    const ensureHandle = (el: HTMLElement, slot: 'main' | 'second'): void => {
      const existing = handleIn(el);
      if (existing) {
        if (el.firstChild !== existing) {
          el.insertBefore(existing, el.firstChild);
        }
        return;
      }
      const handle = createSubtitleDragHandle(slot);
      handle.setAttribute('data-testid', `drag-handle-${slot}`);
      el.insertBefore(handle, el.firstChild);
    };

    if (settings.subtitleSeparation) {
      if (model.cues.length > 0) {
        ensureHandle(container, model.slot);
      } else {
        removeHandle(container);
      }
      return;
    }

    const mainEl = doc.getElementById('mainSub');
    const secondEl = doc.getElementById('secondSub');
    const mainHasCues =
      model.slot === 'main'
        ? model.cues.length > 0
        : Boolean(mainEl?.querySelector('.textBasedSub'));
    const secondHasCues =
      model.slot === 'second'
        ? model.cues.length > 0
        : Boolean(secondEl?.querySelector('.textBasedSub'));

    if (mainHasCues && mainEl) {
      ensureHandle(mainEl, 'main');
      if (secondEl && handleIn(secondEl)) removeHandle(secondEl);
    } else if (secondHasCues && secondEl) {
      ensureHandle(secondEl, 'second');
      if (mainEl && handleIn(mainEl)) removeHandle(mainEl);
    } else {
      removeHandle(mainEl);
      removeHandle(secondEl);
    }
  }
}
